import type { NavbarItem, NavbarSection, UserMenuItem } from '../config/navbar-data'
import type { TenantConfig, User } from '../stores/user-store'

export function checkItemVisibility(
  item: NavbarItem | UserMenuItem,
  user: User | null,
  tenant: TenantConfig | null,
): boolean {
  if (item.requiredRoles && item.requiredRoles.length > 0) {
    if (!user)
      return false
    const hasRequiredRole = item.requiredRoles.some((role) => {
      switch (role) {
        case 'admin':
          return user.admin === true
        case 'superadmin':
          return user.superadmin === true
        case 'rep':
          return user.rep === true
        case 'member':
          return user.member === true
        case 'tenant_admin':
          return user.tenant_admin === true
        default:
          return user.roles.includes(role)
      }
    })
    if (!hasRequiredRole)
      return false
  }

  if (item.requiredCondition) {
    switch (item.requiredCondition) {
      case 'products_enabled':
        return tenant?.products_enabled === true
      case 'rifles_enabled':
        return tenant?.rifles_enabled === true
      case 'courses_enabled':
        return tenant?.courses_enabled === true
      case 'hunts_enabled':
        return tenant?.hunts_enabled === true
      case 'has_subscription':
        return user !== null && user.member === true
      case 'impersonating':
        return user?.impersonating === true
      case 'terms_url':
        return !!tenant?.terms_of_service_url
      default:
        return true
    }
  }

  return true
}

export function filterNavbarSections(
  sections: NavbarSection[],
  user: User | null,
  tenant: TenantConfig | null,
): NavbarSection[] {
  return sections
    .map((section) => {
      if (section.requiredRoles && section.requiredRoles.length > 0) {
        if (!user)
          return null
        const hasRequiredRole = section.requiredRoles.some((role) => {
          switch (role) {
            case 'admin':
              return user.admin === true
            case 'superadmin':
              return user.superadmin === true
            case 'rep':
              return user.rep === true
            case 'member':
              return user.member === true
            case 'tenant_admin':
              return user.tenant_admin === true
            default:
              return user.roles.includes(role)
          }
        })
        if (!hasRequiredRole)
          return null
      }

      if (section.requiredCondition) {
        if (!checkCondition(section.requiredCondition, user, tenant)) {
          return null
        }
      }

      if (section.items) {
        const filteredItems = section.items.filter(item =>
          checkItemVisibility(item, user, tenant),
        )

        if (filteredItems.length === 0 && !section.href) {
          return null
        }

        return {
          ...section,
          items: filteredItems,
        }
      }

      return section
    })
    .filter((section): section is NavbarSection => section !== null)
}

export function filterUserMenuItems(
  items: UserMenuItem[],
  user: User | null,
  tenant: TenantConfig | null,
): UserMenuItem[] {
  return items
    .filter(item => checkItemVisibility(item, user, tenant))
    .map((item) => {
      if (item.requiredCondition === 'terms_url' && tenant?.terms_of_service_url) {
        return {
          ...item,
          href: tenant.terms_of_service_url,
        }
      }
      return item
    })
}

function checkCondition(
  condition: string,
  user: User | null,
  tenant: TenantConfig | null,
): boolean {
  switch (condition) {
    case 'products_enabled':
      return tenant?.products_enabled === true
    case 'rifles_enabled':
      return tenant?.rifles_enabled === true
    case 'courses_enabled':
      return tenant?.courses_enabled === true
    case 'hunts_enabled':
      return tenant?.hunts_enabled === true
    case 'has_subscription':
      return user !== null && user.member === true
    case 'impersonating':
      return user?.impersonating === true
    case 'terms_url':
      return !!tenant?.terms_of_service_url
    default:
      return true
  }
}

export function getActiveRole(user: User | null): string {
  if (!user)
    return 'guest'
  return user.active_role || 'user'
}

export function shouldShowAdminMenu(user: User | null): boolean {
  if (!user)
    return false
  return user.admin === true || user.superadmin === true || user.tenant_admin === true
}

export function shouldShowAdvisorMenu(user: User | null): boolean {
  if (!user)
    return false
  return user.rep === true
}

export function shouldShowMemberCTA(user: User | null, tenant: TenantConfig | null): boolean {
  if (!user || !tenant)
    return false
  return !user.member && !tenant.is_store_only && tenant.hunts_enabled
}

